import { translate } from "@kaltura/mediaspace-shared-utils";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { ChangeEvent, useContext } from "react";
import {
    MediaCard as BaseMediaCard,
    MediaCardContext,
    MediaCardProps as BaseMediaCardProps,
} from "@kaltura/mediaspace-shared-media-list";
import styled from "@emotion/styled";
import { Checkbox } from "@kaltura/ds-react-components";

export const StyledCheckbox = styled(
    Checkbox,
    {
        shouldForwardProp: (prop) => prop !== "inline",
    }
)<{ inline?: boolean }>(({ inline, theme }) => ({
    zIndex: 1,
    marginLeft: theme.spacing(1),
    ...(
        !inline && {
            position: "absolute",
            marginTop: theme.spacing(1),
        }
    ),
}));

export interface MediaCardProps extends Omit<BaseMediaCardProps, "topLeftOverlay"> {
    /**
     * Callback to check if this entry is selected
     * @param entryId - the entry ID to check
     * @returns true if the entry is selected
     */
    isSelected: (entryId: string) => boolean;

    /**
     * selection state change callback
     * @param entryId
     * @param selected
     */
    onChange: (entryId: string, selected: boolean) => void;
}

const baseClassName = "kms-ds-my-media-card";

const MediaCard = (props: MediaCardProps) => {
    const selectableContext = useContext(MediaCardContext);
    const { media, isSelected, onChange, active = false, onMore, reloadMediaList, focused, isWideLayout } = props;
    const { object: entry } = media;

    // Compute selected state using callback
    const selected = isSelected(entry.id) ?? false;

    const sendButtonAnalytics = useButtonAnalytics();
    const sendAnalytics = (name: string, type: ButtonClickAnalyticsType) => {
        sendButtonAnalytics(name, type, entry.id, undefined, "Media Card");
    };

    const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.checked;
        onChange(entry.id, newVal);
        sendAnalytics(`card - Media ${newVal ? "Checked" : "Un-Checked"}`, ButtonClickAnalyticsType.CHOOSE);
    };

    const checkbox = (
        <StyledCheckbox
            onChange={handleCheckboxChange}
            checked={selected}
            className={`${baseClassName}-select-checkbox`}
            color={selectableContext.showThumbnail ? "translucent" : "default"}
            inline={!selectableContext.showThumbnail}
            slotProps={{
                input: {
                    "aria-label": translate("Checkbox for %1 %2", [entry.name, selected ? "Checked" : "Unchecked"]),
                },
            }}
        />
    );

    return (
        <MediaCardContext.Provider value={selectableContext}>
            <BaseMediaCard
                media={media}
                active={active}
                onMore={onMore}
                reloadMediaList={reloadMediaList}
                focused={focused}
                isWideLayout={isWideLayout}
                topLeftOverlay={checkbox}
            />
        </MediaCardContext.Provider>
    );
};

export default MediaCard;
